import { useEffect, useRef, useState } from "react";
import { readClipboardText, writeClipboardText } from "./clipboard";
import { createProductionWebSocketUrl } from "./productionAuth";
import { approveAndProvision, checkProvisioningAndComplete, createProductionPairingInvitation, deriveInviterSas, getPendingProductionRequest, startJoinerPairing, type InviterSession, type PairingSession } from "./productionPairing";
import type { PairingRequest } from "@echopaste/protocol";
import { fetchPersonalHistory, loadPersonalSession, sendPersonalClip, setupPersonalRoom, type PersonalSession, type PersonalClip } from "./personalSync";
import { isTauriRuntime, resolvePlatform } from "./platform";
import { decryptDownloadedMedia, downloadEncryptedChunks, listMediaInbox, listMediaSinks, prepareMediaUpload, uploadPreparedMedia } from "./mediaTransfer";
import { classifyMediaKind } from "./mediaComposer";
import { getVersionedRoomKey, unwrapVersionedRoomKey } from "./storage/db";
import { importKey } from "@echopaste/crypto";
import { sharedFilePayloadToFile } from "./shareTarget";

const API = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
const ROOM_POINTER = "echopaste-personal-room"; // public identifier only; keys remain in IndexedDB
const message = (e: unknown) => e instanceof Error ? e.message : String(e);

export default function PersonalApp() {
  const [session, setSession] = useState<PersonalSession | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState("正在检查连接…");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [deliveryTarget, setDeliveryTarget] = useState<"devices" | "notebook">("devices");
  const [notebookSinks, setNotebookSinks] = useState<Array<{ device_id: string; display_name?: string }>>([]);
  const [selectedNotebookSink, setSelectedNotebookSink] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaProgress, setMediaProgress] = useState("");
  const [serverVersion, setServerVersion] = useState("正在识别服务器…");
  const [mediaInbox, setMediaInbox] = useState<any[]>([]);
  const [clips, setClips] = useState<PersonalClip[]>([]);
  const [setupCode, setSetupCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joiner, setJoiner] = useState<PairingSession | null>(null);
  const [inviter, setInviter] = useState<InviterSession | null>(null);
  const [request, setRequest] = useState<PairingRequest | null>(null);
  const [sas, setSas] = useState("");
  const refresh = useRef<() => Promise<void>>(async () => {});
  const actionLock = useRef(false);

  async function action(work: () => Promise<void>) {
    if (actionLock.current) return;
    actionLock.current = true; setBusy(true); setNotice("");
    try { await work(); } catch (e) { setNotice(message(e)); }
    finally { actionLock.current = false; setBusy(false); }
  }

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        await resolvePlatform();
        const roomId = localStorage.getItem(ROOM_POINTER);
        const restored = roomId ? await loadPersonalSession(roomId) : null;
        if (alive) { setSession(restored); setLoaded(true); }
        const response = await fetch(`${API}/api/health`, { signal: AbortSignal.timeout(10000), cache: "no-store" });
        const data = await response.json();
        if (!response.ok || data.service !== "echopaste") throw new Error("服务器响应异常");
        const infoResponse = await fetch(`${API}/api/v1/info`, { signal: AbortSignal.timeout(10000), cache: "no-store" });
        const info = await infoResponse.json();
        if (alive && infoResponse.ok) setServerVersion(`${info.release || "未知版本"} · ${info.variant || "unknown"} · R2 ${info.capabilities?.r2Storage ? "已启用" : "未启用"}`);
        if (alive && !restored) setStatus("服务器可达 · 等待设备配对");
      } catch (e) { if (alive) { setLoaded(true); setStatus("连接失败"); setNotice(message(e)); } }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!session) return;
    let disposed = false;
    let socket: WebSocket | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let generation = 0;
    let historyGeneration = 0;
    const history = async () => {
      const current = ++historyGeneration;
      const rows = await fetchPersonalHistory(API, session);
      if (!disposed && current === historyGeneration) setClips(rows);
    };
    refresh.current = history;
    const connect = async () => {
      const current = ++generation;
      clearTimeout(retry);
      socket?.close();
      if (disposed) return;
      if (!navigator.onLine) { setStatus("离线 · 恢复网络后自动连接"); return; }
      setStatus("正在连接…");
      try {
        const origin = (API || window.location.origin).replace(/^http/, "ws");
        const url = await createProductionWebSocketUrl({ wsOrigin: origin, identity: session.identity, roomId: session.roomId });
        if (disposed || current !== generation) return;
        const ws = new WebSocket(url); socket = ws;
        ws.onmessage = (event) => {
          if (disposed || current !== generation) return;
          let data: { type: string };
          try { data = JSON.parse(event.data); } catch { return; }
          if (data.type === "connection.ready") { attempt = 0; setStatus("已连接 · 加密同步"); }
          if (["connection.ready", "clip.created", "clip.deleted"].includes(data.type)) void history().catch(e => { if (!disposed) setNotice(message(e)); });
        };
        ws.onclose = () => {
          if (disposed || current !== generation) return;
          setStatus("连接中断 · 自动重连中");
          retry = setTimeout(() => void connect(), Math.min(15000, 1000 * 2 ** attempt++));
        };
        ws.onerror = () => { if (!disposed && current === generation) setStatus("连接失败 · 检查网络或设备配对"); };
      } catch (e) {
        if (!disposed && current === generation) { setNotice(message(e)); setStatus("连接失败"); retry = setTimeout(() => void connect(), 5000); }
      }
    };
    const resume = () => { if (document.visibilityState === "visible") { if (socket?.readyState !== WebSocket.OPEN) void connect(); else void history().catch(e => setNotice(message(e))); } };
    const online = () => void connect();
    void connect();
    window.addEventListener("online", online);
    document.addEventListener("visibilitychange", resume);
    return () => { disposed = true; generation++; clearTimeout(retry); socket?.close(); refresh.current = async () => {}; window.removeEventListener("online", online); document.removeEventListener("visibilitychange", resume); };
  }, [session]);

  useEffect(() => {
    if (!isTauriRuntime()) return;
    let disposed = false;
    let unregister: (() => Promise<void>) | undefined;
    void (async () => {
      if (await resolvePlatform() !== "android" || disposed) return;
      const { invoke, addPluginListener } = await import("@tauri-apps/api/core");
      const receive = (data: { text?: string }) => { if (!disposed && data.text) setText(current => current ? `${current}\n${data.text}` : data.text!); };
      const receiveFile = (data: { name?: unknown; mime?: unknown; base64?: unknown }) => { const file = sharedFilePayloadToFile(data); if (!disposed && file) setMediaFile(file); };
      const listener = await addPluginListener<{ text?: string }>("share-target", "shared-text", receive);
      const fileListener = await addPluginListener<{ name?: unknown; mime?: unknown; base64?: unknown }>("share-target", "shared-file", receiveFile);
      unregister = async () => { await listener.unregister(); await fileListener.unregister(); };
      if (disposed) { await unregister(); return; }
      receive(await invoke("plugin:share-target|consume_shared_text"));
      receiveFile(await invoke("plugin:share-target|consume_shared_file"));
    })().catch(e => { if (!disposed) setNotice(`分享接收不可用：${message(e)}`); });
    return () => { disposed = true; void unregister?.(); };
  }, []);

  const send = async (value: string) => {
    if (!session) throw new Error("请先连接设备。");
    // Preserve the draft if upload fails, including Paste & Send.
    setText(value);
    const sent = await sendPersonalClip(API, session, value);
    setText(current => current === value ? "" : current);
    setClips(current => [{ ...sent, plaintext: value }, ...current.filter(c => c.id !== sent.id)]);
    setNotice("已发送，另一台设备现在可以复制。");
  };
  const findNotebook = async () => {
    if (!session) throw new Error("请先连接设备。");
    const sinks = await listMediaSinks(API, session);
    setNotebookSinks(sinks);
    if (sinks.length === 1) setSelectedNotebookSink(sinks[0].device_id);
    if (!sinks.length) throw new Error("尚未找到 Notebook 接收主机。请先让 Notebook 完成配对并注册接收能力。");
  };
  const sendToNotebook = async (value: string) => {
    if (!session) throw new Error("请先连接设备。");
    const sinks = notebookSinks.length ? notebookSinks : await listMediaSinks(API, session);
    setNotebookSinks(sinks);
    const recipientDeviceId = selectedNotebookSink || (sinks.length === 1 ? sinks[0].device_id : "");
    if (!recipientDeviceId) throw new Error(sinks.length ? "请选择 Notebook 接收主机。" : "尚未找到 Notebook 接收主机。请先完成 Notebook 配对。");
    const bytes = new TextEncoder().encode(value);
    const prepared = await prepareMediaUpload({ bytes, text: value, kind: "text", mime: "text/plain;charset=utf-8", mode: "notebook_delivery", recipientDeviceId, roomId: session.roomId, roomKey: session.key, keyVersion: session.keyVersion });
    setText(value);
    await uploadPreparedMedia(API, session, prepared);
    setText(current => current === value ? "" : current);
    setNotice("已进入云端待处理队列。Notebook 主机联网并解锁后会保存；最长保留 7 天。");
  };
  const resolveNotebookSink = async () => {
    if (!session) throw new Error("请先连接设备。");
    const sinks = notebookSinks.length ? notebookSinks : await listMediaSinks(API, session);
    setNotebookSinks(sinks);
    const recipientDeviceId = selectedNotebookSink || (sinks.length === 1 ? sinks[0].device_id : "");
    if (!recipientDeviceId) throw new Error(sinks.length ? "请选择 Notebook 接收主机。" : "尚未找到 Notebook 接收主机。请先完成 Notebook 配对。");
    return recipientDeviceId;
  };
  const sendMediaFile = async () => {
    if (!session || !mediaFile) throw new Error("请先选择图片或文件。");
    const recipientDeviceId = deliveryTarget === "notebook" ? await resolveNotebookSink() : undefined;
    const prepared = await prepareMediaUpload({
      bytes: new Uint8Array(await mediaFile.arrayBuffer()),
      kind: classifyMediaKind(mediaFile.type),
      name: mediaFile.name,
      mime: mediaFile.type || "application/octet-stream",
      mode: deliveryTarget === "notebook" ? "notebook_delivery" : "room_broadcast",
      ...(recipientDeviceId ? { recipientDeviceId } : {}),
      roomId: session.roomId,
      roomKey: session.key,
      keyVersion: session.keyVersion,
    });
    setMediaProgress(`正在上传 0/${prepared.encryptedChunks.length}`);
    await uploadPreparedMedia(API, session, prepared, (done, total) => setMediaProgress(`正在上传 ${done}/${total}`));
    setMediaFile(null); setMediaProgress("");
    setNotice(deliveryTarget === "notebook" ? "已进入云端待处理队列。Notebook 主机联网并解锁后会保存；最长保留 7 天。" : "已加密发送，其他设备可在 24 小时内接收。");
  };
  const refreshMediaInbox = async () => {
    if (!session) throw new Error("请先连接设备。");
    setMediaInbox(await listMediaInbox(API, session));
  };
  const receiveMediaItem = async (item: any) => {
    if (!session) throw new Error("请先连接设备。");
    let roomKey = session.key;
    if (item.keyVersion !== session.keyVersion) {
      const historical = await getVersionedRoomKey(session.roomId, item.keyVersion);
      if (!historical) throw new Error("缺少该项目使用的历史房间密钥。");
      roomKey = await importKey(await unwrapVersionedRoomKey(historical), { extractable: false });
    }
    const encryptedChunks = await downloadEncryptedChunks(API, session, item);
    const decoded = await decryptDownloadedMedia({ item, encryptedChunks, roomId: session.roomId, roomKey });
    if (decoded.manifest.kind === "text") {
      setText(new TextDecoder().decode(decoded.bytes)); setNotice("文字已安全解密到输入框。"); return;
    }
    const url = URL.createObjectURL(new Blob([decoded.bytes as unknown as BlobPart], { type: decoded.manifest.mime || "application/octet-stream" }));
    const anchor = document.createElement("a"); anchor.href = url; anchor.download = decoded.manifest.name || (decoded.manifest.kind === "image" ? "echopaste-image" : "echopaste-file"); anchor.click(); URL.revokeObjectURL(url);
    setNotice("文件已安全解密并开始下载。");
  };
  const sendSelected = (value: string) => deliveryTarget === "notebook" ? sendToNotebook(value) : send(value);
  const copy = async (value: string) => { await writeClipboardText(value); setNotice("已复制，可以切换到其他应用粘贴。"); };
  const latest = clips.find(c => !c.unavailable);

  return <div className="app-container personal-app">
    <header className="app-header"><h1 className="brand-title">EchoPaste</h1><span className="personal-status" role="status">{status}</span></header>
    <p className="personal-subtitle">手机与电脑之间，复制 · 发送 · 粘贴</p>
    {notice && <div className="personal-notice" role="alert">{notice}</div>}
    {!loaded ? <p>正在读取本机设备信息…</p> : !session ? <>
      <section className="info-card personal-setup-card"><h2 className="info-title">第一次使用：初始化电脑</h2>
        <p>从电脑上的 <code>OWNER_SETUP_SECRET.txt</code> 复制首次设置码，粘贴到下面，然后创建同步空间。此操作只需执行一次。</p>
        <input className="input-text" type="password" autoComplete="off" aria-label="首次设置码" placeholder="粘贴首次设置码" value={setupCode} onChange={e => setSetupCode(e.target.value)} />
        <button className="btn-primary" disabled={busy || !setupCode.trim()} onClick={() => void action(async () => {
          const roomId = localStorage.getItem(ROOM_POINTER) || crypto.randomUUID();
          localStorage.setItem(ROOM_POINTER, roomId);
          setSession(await setupPersonalRoom(API, roomId, setupCode)); setSetupCode("");
        })}>初始化并创建同步空间</button>
      </section>
      <section className="info-card"><h2 className="info-title">连接这台设备</h2>
        <p>如果你已经在另一台设备完成初始化，请在那里生成邀请码，然后粘贴到这里。</p>
        <textarea className="input-text" aria-label="邀请码" placeholder="EP2.…" value={joinCode} onChange={e => setJoinCode(e.target.value)} />
        <div className="personal-actions"><button className="btn-secondary" disabled={busy} onClick={() => void action(async () => setJoinCode(await readClipboardText()))}>粘贴邀请码</button>
          <button className="btn-primary" disabled={busy || !joinCode.trim()} onClick={() => void action(async () => { setJoiner(await startJoinerPairing(API, joinCode)); setJoinCode(""); })}>请求连接</button></div>
        {joiner && <div className="personal-pairing"><p>请核对两台设备显示的数字完全一致：</p><strong className="personal-sas">{joiner.sas}</strong><p>在另一台设备点“核对一致，允许连接”后，点下面的按钮。</p>
          <button className="btn-primary" disabled={busy} onClick={() => void action(async () => {
            const result = await checkProvisioningAndComplete(API, joiner);
            if (result.status === "pending") { setNotice("等待另一台设备确认，请确认后再点一次。"); return; }
            localStorage.setItem(ROOM_POINTER, result.roomId); setSession(await loadPersonalSession(result.roomId)); setJoiner(null);
          })}>完成连接</button></div>}
      </section>
    </> : <>
      <section className="composer-card"><textarea className="composer-textarea" aria-label="要发送的文字" placeholder="粘贴或输入要发送的文字…" value={text} onChange={e => setText(e.target.value)} />
        <div className="personal-actions"><label><input type="radio" name="delivery-target" checked={deliveryTarget === "devices"} onChange={() => setDeliveryTarget("devices")} /> 发送到设备</label><label><input type="radio" name="delivery-target" checked={deliveryTarget === "notebook"} onChange={() => setDeliveryTarget("notebook")} /> 保存到 Notebook</label></div>
        {deliveryTarget === "notebook" && <div className="personal-actions"><button className="btn-secondary" disabled={busy} onClick={() => void action(findNotebook)}>查找 Notebook</button><select aria-label="Notebook 接收主机" value={selectedNotebookSink} onChange={e => setSelectedNotebookSink(e.target.value)}><option value="">选择接收主机</option>{notebookSinks.map(sink => <option key={sink.device_id} value={sink.device_id}>{sink.display_name || sink.device_id}</option>)}</select></div>}
        <div className="personal-actions"><button className="btn-secondary" disabled={busy} onClick={() => void action(async () => setText(await readClipboardText()))}>粘贴</button><button className="btn-secondary" disabled={busy} onClick={() => void action(async () => sendSelected(await readClipboardText()))}>粘贴并发送</button><button className="btn-primary" disabled={busy || !text.trim()} onClick={() => void action(() => sendSelected(text))}>发送</button></div>
        <div className="personal-actions"><label className="btn-secondary">选择图片或文件<input hidden type="file" onChange={e => setMediaFile(e.target.files?.[0] ?? null)} /></label><button className="btn-primary" disabled={busy || !mediaFile} onClick={() => void action(sendMediaFile)}>发送文件</button><small>{mediaFile ? `${mediaFile.name} · ${(mediaFile.size / 1024 / 1024).toFixed(2)} MiB` : "图片或文件最多 25 MiB"}</small></div>
        {mediaProgress && <div className="personal-notice" role="status">{mediaProgress}</div>}
      </section>
      <div className="personal-actions"><h2 className="info-title">最近的文字</h2><button className="btn-secondary" disabled={busy || !latest} onClick={() => void action(() => copy(latest!.plaintext))}>复制最新</button><button className="btn-secondary" disabled={busy} onClick={() => void action(refresh.current)}>刷新</button></div>
      {clips.length === 0 && <p>还没有文字。试着从另一台设备发送一条。</p>}
      <div className="personal-clips">{clips.map(clip => <article className="info-card" key={clip.id}><p className="personal-clip-text">{clip.plaintext}</p><div className="personal-actions"><small>{clip.originDeviceId === session.identity.deviceId ? "本机发送" : "其他设备"} · {new Date(clip.createdAt).toLocaleString()}</small><button className="btn-secondary" disabled={busy || clip.unavailable} onClick={() => void action(() => copy(clip.plaintext))}>复制</button></div></article>)}</div>
      <section className="info-card"><div className="personal-actions"><h2 className="info-title">收到的图片和文件</h2><button className="btn-secondary" disabled={busy} onClick={() => void action(refreshMediaInbox)}>刷新媒体</button></div>{mediaInbox.length === 0 ? <p>暂无媒体；需要时点击刷新。</p> : mediaInbox.map(item => <div className="personal-actions" key={item.id}><small>{item.originDeviceId === session.identity.deviceId ? "本机发送" : "其他设备"} · {new Date(item.createdAt).toLocaleString()}</small><button className="btn-secondary" disabled={busy} onClick={() => void action(() => receiveMediaItem(item))}>解密并保存</button></div>)}</section>
      <details className="info-card"><summary>连接另一台设备</summary><p>邀请码有效期 10 分钟。新设备请求后，请核对两边的数字。</p>
        <button className="btn-secondary" disabled={busy} onClick={() => void action(async () => { setInviter(await createProductionPairingInvitation(API, session.roomId)); setRequest(null); setSas(""); })}>生成邀请码</button>
        {inviter && <div className="personal-pairing"><textarea className="input-text" aria-label="生成的邀请码" readOnly value={inviter.invitationCode} /><div className="personal-actions"><button className="btn-secondary" disabled={busy} onClick={() => void action(() => copy(inviter.invitationCode))}>复制邀请码</button><button className="btn-secondary" disabled={busy} onClick={() => void action(async () => { const pending = await getPendingProductionRequest(API, inviter); setRequest(pending); setSas(pending ? await deriveInviterSas(inviter, pending) : ""); if (!pending) setNotice("尚未收到请求。请在新设备输入邀请码并点请求连接。"); })}>查看连接请求</button></div>
          {request && <><strong className="personal-sas">{sas}</strong><p>只有两台设备数字完全一致时才允许连接。</p><button className="btn-primary" disabled={busy} onClick={() => void action(async () => { await approveAndProvision(API, inviter, request); setRequest(null); setInviter(null); setNotice("已允许。请在新设备点“完成连接”。"); })}>核对一致，允许连接</button></>}
        </div>}
      </details>
    </>}
    <footer className="personal-footer">文字、图片和文件均在本机加密；设备同步保留 24 小时，Notebook 待处理队列最长 7 天。<br />客户端：个人版 2.0 R2 · 服务器：{API || window.location.origin} · {serverVersion}</footer>
  </div>;
}
